import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { AuditChip } from '@/components/audit/AuditChip';
import { useApiError } from '@/lib/useApiError';
import { useUpsertWaitlistDailyMetrics, useWaitlistMetrics } from '@/queries/phase2';
import {
  waitlistDailyMetricsRequestSchema,
  type WaitlistDailyMetricsRequest,
} from '@/schemas/admin/phase2/waitlist';
import { formatNumber } from '@/lib/dates';
import { useUiStore } from '@/stores/ui';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_authed/phase2/waitlist/metrics')({
  component: WaitlistMetricsScreen,
});

function WaitlistMetricsScreen() {
  const { t } = useTranslation('phase2');
  const metrics = useWaitlistMetrics();
  const resolveError = useApiError();
  const [open, setOpen] = React.useState(false);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('metrics.title', { defaultValue: 'Waitlist metrics' })}
          </h1>
          <p className="max-w-2xl text-sm text-[color:var(--color-fg-muted)]">
            {t('metrics.subtitle', {
              defaultValue:
                'Three strategic numbers (Reach, Engagement, Waitlist) plus the 30-day trend. Followers + FB Group are typed by ops; waitlist totals come from the live table.',
            })}
          </p>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Plus className="size-4" aria-hidden />
          {t('metrics.addDaily', { defaultValue: 'Log daily metrics' })}
        </Button>
      </header>

      {metrics.error ? (
        <Card>
          <CardContent className="p-6 text-sm text-[color:var(--color-danger)]">
            {resolveError(metrics.error).message}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <BigStat
          label={t('metrics.reach', { defaultValue: 'Reach' })}
          value={metrics.data?.daily_series?.[metrics.data.daily_series.length - 1]?.followers_total ?? null}
          hint={t('metrics.reachHint', { defaultValue: 'Followers across socials.' })}
          loading={metrics.isLoading}
        />
        <BigStat
          label={t('metrics.engagement', { defaultValue: 'Engagement' })}
          value={metrics.data?.daily_series?.[metrics.data.daily_series.length - 1]?.fb_group_active ?? null}
          hint={t('metrics.engagementHint', { defaultValue: 'Active FB group members.' })}
          loading={metrics.isLoading}
        />
        <BigStat
          label={t('metrics.waitlist', { defaultValue: 'Waitlist' })}
          value={metrics.data?.waitlist_active ?? null}
          hint={t('metrics.waitlistHint', { defaultValue: 'Pending + verified.' })}
          loading={metrics.isLoading}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardContent className="space-y-3 pt-6">
            <h2 className="text-base font-semibold">
              {t('metrics.foundingTitle', { defaultValue: 'Founding members' })}
            </h2>
            <p className="text-xs text-[color:var(--color-fg-muted)]">
              {t('metrics.foundingHint', {
                defaultValue:
                  'First 1,000 verified members get the founding badge. Counter is server-driven.',
              })}
            </p>
            <FoundingBar
              awarded={metrics.data?.founding_awarded ?? 0}
              cap={metrics.data?.founding_cap ?? 1000}
            />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-3 pt-6">
            <h2 className="text-base font-semibold">
              {t('metrics.breakdownTitle', { defaultValue: 'By state' })}
            </h2>
            <ul className="grid grid-cols-2 gap-2 text-sm">
              <Stat label={t('waitlist.states.pending', { defaultValue: 'Pending' })} value={metrics.data?.waitlist_pending} />
              <Stat label={t('waitlist.states.verified', { defaultValue: 'Verified' })} value={metrics.data?.waitlist_verified} />
              <Stat label={t('waitlist.states.converted', { defaultValue: 'Converted' })} value={metrics.data?.waitlist_converted} />
              <Stat
                label={t('metrics.launchMode', { defaultValue: 'Launch mode' })}
                value={metrics.data?.launch_mode ?? null}
                isText
              />
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="space-y-3 pt-6">
          <h2 className="text-base font-semibold">
            {t('metrics.trendTitle', { defaultValue: '30-day trend' })}
          </h2>
          <DailySeriesChart data={metrics.data?.daily_series ?? []} />
        </CardContent>
      </Card>

      <DailyMetricsDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

function BigStat({
  label,
  value,
  hint,
  loading,
}: {
  label: React.ReactNode;
  value: number | null;
  hint: React.ReactNode;
  loading: boolean;
}) {
  const locale = useUiStore((s) => s.locale);
  return (
    <Card>
      <CardContent className="space-y-1 pt-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)]">
          {label}
        </p>
        <p
          dir="ltr"
          className="text-3xl font-semibold tabular-nums text-[color:var(--color-fg)]"
        >
          {loading ? <span className="inline-block h-8 w-24 animate-pulse rounded bg-[color:var(--color-surface-2)]" /> : value == null ? '—' : formatNumber(value, locale)}
        </p>
        <p className="text-xs text-[color:var(--color-fg-muted)]">{hint}</p>
      </CardContent>
    </Card>
  );
}

function Stat({
  label,
  value,
  isText,
}: {
  label: React.ReactNode;
  value: number | string | null | undefined;
  isText?: boolean;
}) {
  const locale = useUiStore((s) => s.locale);
  return (
    <li className="flex items-center justify-between gap-3 rounded-[var(--radius-sm)] bg-[color:var(--color-surface-2)] px-3 py-2">
      <span className="text-[color:var(--color-fg-muted)]">{label}</span>
      <span dir="ltr" className="font-mono">
        {value == null
          ? '—'
          : isText
            ? String(value)
            : formatNumber(typeof value === 'number' ? value : Number(value), locale)}
      </span>
    </li>
  );
}

function FoundingBar({ awarded, cap }: { awarded: number; cap: number }) {
  const safeCap = cap > 0 ? cap : 1;
  const pct = Math.min(100, Math.round((awarded / safeCap) * 100));
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium">
          <span dir="ltr">{awarded}</span> / <span dir="ltr">{cap}</span>
        </span>
        <Badge tone={pct >= 80 ? 'warn' : 'accent'}>{pct}%</Badge>
      </div>
      <div className="h-2 overflow-hidden rounded-[var(--radius-pill)] bg-[color:var(--color-surface-2)]" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div
          className={cn(
            'h-full',
            pct >= 80 ? 'bg-[color:var(--color-warn)]' : 'bg-[color:var(--color-accent)]'
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

interface DailyPoint {
  day: string;
  followers_total?: number | null | undefined;
  fb_group_active?: number | null | undefined;
  waitlist_total?: number | null | undefined;
  waitlist_new?: number | null | undefined;
}

function DailySeriesChart({ data }: { data: readonly DailyPoint[] }) {
  const { t } = useTranslation('phase2');
  if (data.length === 0) {
    return (
      <p className="text-sm text-[color:var(--color-fg-muted)]">
        {t('metrics.trendEmpty', {
          defaultValue: 'No daily metrics logged yet — log one to start the trend.',
        })}
      </p>
    );
  }
  const series = [
    {
      key: 'followers_total' as const,
      label: 'Followers',
      stroke: 'var(--brand-purple)',
    },
    {
      key: 'fb_group_active' as const,
      label: 'FB group',
      stroke: 'var(--brand-orange)',
    },
    {
      key: 'waitlist_total' as const,
      label: 'Waitlist',
      stroke: 'var(--brand-pink)',
    },
  ];
  const maxVal = Math.max(
    1,
    ...data.flatMap((d) => series.map((s) => Number(d[s.key] ?? 0)))
  );
  const width = 600;
  const height = 160;
  const stepX = data.length > 1 ? width / (data.length - 1) : 0;

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="h-40 w-full"
        role="img"
        aria-label="Daily metrics trend"
      >
        {series.map((s) => {
          const points = data
            .map((d, i) => {
              const v = Number(d[s.key] ?? 0);
              const x = i * stepX;
              const y = height - (v / maxVal) * (height - 8) - 4;
              return `${x},${y}`;
            })
            .join(' ');
          return (
            <polyline
              key={s.key}
              points={points}
              fill="none"
              stroke={s.stroke}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          );
        })}
      </svg>
      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
        {series.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5">
            <span
              aria-hidden
              className="inline-block size-3 rounded-full"
              style={{ backgroundColor: s.stroke }}
            />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function DailyMetricsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('phase2');
  const upsert = useUpsertWaitlistDailyMetrics();
  const resolveError = useApiError();

  const today = new Date().toISOString().slice(0, 10);
  const [day, setDay] = React.useState(today);
  const [followers, setFollowers] = React.useState('');
  const [fb, setFb] = React.useState('');
  const [waitlistTotal, setWaitlistTotal] = React.useState('');
  const [waitlistNew, setWaitlistNew] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (!open) {
      setDay(today);
      setFollowers('');
      setFb('');
      setWaitlistTotal('');
      setWaitlistNew('');
      setNotes('');
      setErrors({});
    }
  }, [open, today]);

  function maybeNum(v: string): number | undefined {
    const n = Number(v);
    if (v === '' || Number.isNaN(n)) return undefined;
    return n;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const candidate: WaitlistDailyMetricsRequest = (() => {
      const base: WaitlistDailyMetricsRequest = { day };
      const f = maybeNum(followers);
      if (typeof f === 'number') base.followers_total = f;
      const fbn = maybeNum(fb);
      if (typeof fbn === 'number') base.fb_group_active = fbn;
      const wt = maybeNum(waitlistTotal);
      if (typeof wt === 'number') base.waitlist_total = wt;
      const wn = maybeNum(waitlistNew);
      if (typeof wn === 'number') base.waitlist_new = wn;
      if (notes.trim()) base.notes = notes.trim();
      return base;
    })();
    const parsed = waitlistDailyMetricsRequestSchema.safeParse(candidate);
    if (!parsed.success) {
      const issues: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path.join('.');
        issues[key] ??= issue.message;
      }
      setErrors(issues);
      return;
    }
    setErrors({});
    try {
      await upsert.mutateAsync(parsed.data);
      toast.success(t('metrics.savedTitle', { defaultValue: 'Daily metrics saved.' }));
      onOpenChange(false);
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(resolved.message, resolved.recovery ? { description: resolved.recovery } : undefined);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('metrics.dialogTitle', { defaultValue: 'Log daily metrics' })}</DialogTitle>
          <DialogDescription>
            {t('metrics.dialogBody', {
              defaultValue:
                'Upserts a row keyed on the date. Re-saving overwrites existing values for that day.',
            })}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="metrics-day">{t('metrics.day', { defaultValue: 'Day' })}</Label>
            <Input
              id="metrics-day"
              type="date"
              dir="ltr"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              aria-invalid={Boolean(errors.day)}
            />
            {errors.day ? (
              <p role="alert" className="mt-1 text-xs text-[color:var(--color-danger)]">
                {errors.day}
              </p>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <NumField id="metrics-followers" label={t('metrics.followers', { defaultValue: 'Followers' })} value={followers} onChange={setFollowers} />
            <NumField id="metrics-fb" label={t('metrics.fb', { defaultValue: 'FB group active' })} value={fb} onChange={setFb} />
            <NumField id="metrics-waitlist-total" label={t('metrics.waitlistTotal', { defaultValue: 'Waitlist total' })} value={waitlistTotal} onChange={setWaitlistTotal} />
            <NumField id="metrics-waitlist-new" label={t('metrics.waitlistNew', { defaultValue: 'Waitlist new' })} value={waitlistNew} onChange={setWaitlistNew} />
          </div>
          <div>
            <Label htmlFor="metrics-notes">{t('metrics.notes', { defaultValue: 'Notes' })}</Label>
            <textarea
              id="metrics-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={500}
              className="block w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)] focus-visible:ring-offset-2"
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            <AuditChip />
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                {t('actions.cancel', { ns: 'common', defaultValue: 'Cancel' })}
              </Button>
              <Button type="submit" loading={upsert.isPending}>
                {t('metrics.save', { defaultValue: 'Save metrics' })}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NumField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        min={0}
        inputMode="numeric"
        dir="ltr"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="font-mono"
      />
    </div>
  );
}
